import * as ngCore from '@angular/core'

import * as layout from '../layout'

import * as content from './content'
import * as tableEc from './ec-plugin'
import type * as models from './models'
import { Store } from './store'
import * as toolbar from './toolbar'

/**
 * Canonical table surface for standalone and entity-collection configuration.
 *
 * One required input is either a complete core config or an EC config carrying
 * a collection plus this component instance's view options. Its scoped store
 * receives only complete core snapshots and keeps interaction state isolated.
 */
@ngCore.Component({
  imports: [content.Component, layout.widget.Component, toolbar.Component],
  providers: [Store],
  selector: 'atn-ds-table',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component<TRow = unknown> {
  /** Complete standalone or entity-collection-backed table configuration. */
  public readonly configSg = ngCore.input.required<
    models.Config<TRow> | tableEc.Config.Input.ForTable
  >({
    alias: 'config',
  })

  /**
   * Collection identity is isolated from the full input so replacing only view
   * options does not create another mapper or duplicate its signal graph.
   */
  private readonly _collectionSg = ngCore.computed(
    (config = this.configSg()) =>
      'collection' in config ? config.collection : undefined,
  )

  /** Current EC view options, independently reactive from collection identity. */
  private readonly _viewSg = ngCore.computed((config = this.configSg()) =>
    'collection' in config ? config.view : undefined,
  )

  /**
   * Creates one mapper for the active collection. That mapper consumes
   * {@link _viewSg}, so immutable view changes update the existing mapper
   * while the collection remains the same.
   */
  private readonly _collectionConfigSg = ngCore.computed(
    (collection = this._collectionSg()) =>
      collection ? tableEc.createConfigSg(collection, this._viewSg) : undefined,
  )

  /** Scoped owner of this table's prepared and interaction state. */
  public readonly store = ngCore.inject(Store<TRow>)

  /** Keeps the scoped store aligned with the active table input. */
  constructor() {
    ngCore.effect(() => {
      const config = this.configSg()
      if ('collection' in config) {
        this.store.setConfig(this._collectionConfigSg()?.())
        return
      }

      this.store.setConfig(config)
    })
  }
}
